/**
 * Geocodificador especializado para infraestructuras de seguridad en Andalucía
 * 
 * Conecta con servicios WFS DERA G12 (Datos Espaciales de Referencia de Andalucía) para acceder a:
 * - Policía Nacional y Local (~40 comisarías en Andalucía)
 * - Cuarteles Guardia Civil (~120 en Andalucía)
 * - Parques de Bomberos (~86 en Andalucía según IECA)
 * - Centros de Gestión de Emergencias (112)
 * 
 * Fuentes oficiales:
 * - WFS DERA G12: https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * - Capas: g12_26_Policia, g12_34_GuardiaCivil, g12_29_ParqueBomberos, g12_35_GestionEmergencias
 * 
 * NOTA: El endpoint ISE_seguridad/wfs está OBSOLETO (404). Usar DERA_g12_servicios.
 * 
 * @module services/geocoding/specialized
 * @updated 2026-01-24 - Migración a DERA_g12_servicios (F7.T7)
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de infraestructuras de seguridad
 */
export enum SecurityFacilityType {
  /** Comisarías Policía Nacional */
  POLICE_STATION = 'COMISARIA',
  
  /** Cuarteles Guardia Civil */
  CIVIL_GUARD = 'CUARTEL_GC',
  
  /** Instalaciones Policía Local */
  LOCAL_POLICE = 'POLICIA_LOCAL',
  
  /** Parques de Bomberos */
  FIRE_STATION = 'PARQUE_BOMBEROS',
  
  /** Centros 112 / Emergencias */
  EMERGENCY_CENTER = 'CENTRO_112'
}

/**
 * Opciones de búsqueda específicas para seguridad
 */
export interface SecuritySearchOptions extends WFSSearchOptions {
  /** Tipo de instalación de seguridad */
  facilityType?: SecurityFacilityType;
  
  /** Código de unidad (si disponible) */
  unitCode?: string;
}

/**
 * Geocodificador especializado para infraestructuras de seguridad andaluzas
 * 
 * Precisión esperada: ±10-20m (coordenadas oficiales ISE/IECA)
 * Cobertura: ~250 instalaciones seguridad + bomberos
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSSecurityGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Comisaría Provincial Granada',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * // result.confidence >= 80 (match oficial ISE)
 * ```
 */
export class WFSSecurityGeocoder extends WFSBaseGeocoder {
  
  /** Endpoint unificado DERA G12 Servicios (F7.T7) */
  private static readonly DERA_G12_ENDPOINT = 'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs';
  
  /** Capas de seguridad disponibles en DERA G12 */
  private static readonly LAYER_POLICIA = 'g12_26_Policia';
  private static readonly LAYER_GUARDIA_CIVIL = 'g12_34_GuardiaCivil';
  private static readonly LAYER_BOMBEROS = 'g12_29_ParqueBomberos';
  private static readonly LAYER_EMERGENCIAS = 'g12_35_GestionEmergencias';
  
  /**
   * Configuración específica para WFS DERA G12 Seguridad
   * NOTA: Migrado de ISE_seguridad (obsoleto, 404) a DERA_g12_servicios
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: WFSSecurityGeocoder.DERA_G12_ENDPOINT,
      layerName: WFSSecurityGeocoder.LAYER_GUARDIA_CIVIL, // Capa por defecto: Guardia Civil (más común en PTEL)
      fuzzyThreshold: 0.35, // Threshold estricto (nombres oficiales únicos)
      timeout: 15000,
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Parsea feature GeoJSON de DERA G12 Seguridad
   *
   * Schema actual DERA G12 (verificado vía DescribeFeatureType + smoke real 15-may-2026,
   * sub-WSSD `OBS-FT-WFS-SECURITY-SCHEMA-DRIFT`):
   * {
   *   type: "Feature",
   *   geometry: { type: "Point" | "MultiPoint", coordinates: [...] },
   *   properties: {
   *     nombre: "PAUT GRANADA",            // antes mayúsculas DENOMINACION (drift schema)
   *     tipo: "...",
   *     dependencia: "Policía Nacional",   // antes ORGANISMO; campo análogo en G12
   *     municipio: "Granada",              // antes MUNICIPIO
   *     localidad: "Granada",
   *     provincia: "Granada",              // antes PROVINCIA
   *     cod_mun: "18087",                  // código INE municipal (no existía en ISE)
   *     direccion: "PLAZA CAMPO VERDE, 2", // antes DIRECCION
   *     telefono: "671539130",             // antes TELEFONO
   *     telefono2: "...",
   *     correo: "...",
   *     id_dera: 12345                     // antes CODIGO_UNIDAD; entero único DERA
   *   }
   * }
   *
   * NOTA: Mantenemos las claves mayúsculas legacy ISE como fallback defensivo al final
   * de cada `getStringProp` (coste 0, anti-regresión cross-endpoint).
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Validar geometría vía parseGeometry (Point + MultiPoint N=1 — sub-0a)
      const pt = this.parseGeometry(geom, `Security feature ${String(feature.id ?? 'sin-id')}`);
      if (!pt) {
        console.warn('Feature seguridad sin geometría válida:', feature.id);
        return null;
      }

      const [x, y] = pt;

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      return {
        name: getStringProp(props, 'nombre', 'DENOMINACION', 'NOMBRE'),
        x,
        y,
        municipality: getStringProp(props, 'municipio', 'localidad', 'MUNICIPIO', 'LOCALIDAD'),
        province: getStringProp(props, 'provincia', 'PROVINCIA'),
        address: getStringProp(props, 'direccion', 'DIRECCION', 'DOMICILIO'),
        properties: {
          type: getStringProp(props, 'tipo', 'TIPO'),
          organism: getStringProp(props, 'dependencia', 'ORGANISMO', 'CUERPO'),
          unitCode: getStringProp(props, 'id_dera', 'CODIGO_UNIDAD', 'CODIGO'),
          phone: getStringProp(props, 'telefono', 'telefono2', 'TELEFONO'),
          // emergency: campo eliminado del schema DERA G12 (sub-WSSD 15-may); preservable via ...props
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature seguridad:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para infraestructuras de seguridad
   *
   * Override completo (NO invoca super) — el filtro base usa claves mayúsculas
   * `MUNICIPIO ILIKE` / `PROVINCIA ILIKE` que GeoServer DERA G12 rechaza con HTTP 400
   * (smoke 15-may: case-sensitive en CQL property names).
   *
   * sub-WSSD: prioriza `cod_mun='X'` cuando se aporta `codMun` (filtro exacto INE),
   * fallback a `municipio ILIKE '%X%'` con la string libre. `CODIGO_UNIDAD =` se degrada
   * a `dependencia ILIKE` (CODIGO_UNIDAD no existe en schema DERA G12; `dependencia` es
   * el campo de texto análogo). BBOX se reaplica con la misma sintaxis de la base.
   */
  protected buildCQLFilter(options: SecuritySearchOptions): string {
    const filters: string[] = [];

    // Filtro municipal: cod_mun (INE) preferente sobre municipio ILIKE
    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    } else if (options.municipality) {
      filters.push(`municipio ILIKE '%${this.escapeCQL(options.municipality)}%'`);
    }

    // Filtro provincial
    if (options.province) {
      filters.push(`provincia ILIKE '%${this.escapeCQL(options.province)}%'`);
    }

    // Filtro BBOX (heredado de la base, sintaxis idéntica)
    if (options.bbox) {
      const [minX, minY, maxX, maxY] = options.bbox;
      filters.push(`BBOX(the_geom,${minX},${minY},${maxX},${maxY},'EPSG:25830')`);
    }

    // Filtro por tipo de instalación
    // Whitelist HX-WHITELIST-FIELD-PER-LAYER (sub-WSSDV2 22-may): SOLO LAYER_POLICIA
    // y LAYER_EMERGENCIAS exponen el campo `tipo` en DERA G12 (verificado DescribeFeatureType
    // F2_SEG_SUBA2 18-may PASO 0.5 P3). Emitir `tipo ILIKE` sobre LAYER_GUARDIA_CIVIL o
    // LAYER_BOMBEROS produciría HTTP 400 silencioso enmascarado por orchestrator fallback.
    // Bug latente inactivo en producción actual (0 consumers productivos inyectan facilityType
    // en path SEG, Orchestrator L3157 switch no propaga). Fix defensivo anti-regresión futura.
    const LAYERS_WITH_TIPO_SEG: ReadonlySet<string> = new Set([
      WFSSecurityGeocoder.LAYER_POLICIA,
      WFSSecurityGeocoder.LAYER_EMERGENCIAS,
    ]);
    if (options.facilityType && LAYERS_WITH_TIPO_SEG.has(this.config.layerName)) {
      const typeValue = this.getFacilityTypeValue(options.facilityType);
      filters.push(`tipo ILIKE '%${this.escapeCQL(typeValue)}%'`);
    }

    // unitCode: degradado a `dependencia ILIKE` (CODIGO_UNIDAD no existe en DERA G12)
    if (options.unitCode) {
      filters.push(`dependencia ILIKE '%${this.escapeCQL(options.unitCode)}%'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Convierte tipo de facilidad a valor para WFS
   */
  private getFacilityTypeValue(type: SecurityFacilityType): string {
    const map: Record<SecurityFacilityType, string> = {
      [SecurityFacilityType.POLICE_STATION]: 'Comisaría',
      [SecurityFacilityType.CIVIL_GUARD]: 'Cuartel',
      [SecurityFacilityType.LOCAL_POLICE]: 'Policía Local',
      [SecurityFacilityType.FIRE_STATION]: 'Parque Bomberos',
      [SecurityFacilityType.EMERGENCY_CENTER]: 'Centro 112'
    };
    return map[type] || '';
  }

  /**
   * Geocodifica cambiando automáticamente entre capas según tipo detectado
   * 
   * Estrategia (F7.T7 - Usando DERA_g12_servicios unificado):
   * 1. Detecta tipo de infraestructura por palabras clave
   * 2. Prioriza capa específica según tipo detectado
   * 3. Fallback a otras capas de seguridad si no encuentra
   * 
   * Capas disponibles en DERA G12:
   * - g12_26_Policia: Policía Nacional y Local
   * - g12_34_GuardiaCivil: Cuarteles Guardia Civil
   * - g12_29_ParqueBomberos: Parques de Bomberos
   * - g12_35_GestionEmergencias: Centros 112 y emergencias
   */
  public async geocodeWithAutoLayer(options: SecuritySearchOptions): Promise<GeocodingResult | null> {
    const nameLower = options.name.toLowerCase();

    // Determinar capa óptima según nombre (todas usan DERA_g12_servicios)
    const layerPriority: string[] = [];

    // Policía Nacional/Local
    if (nameLower.includes('comisaría') || nameLower.includes('policía nacional') ||
        nameLower.includes('policía local') || nameLower.includes('municipal')) {
      layerPriority.push(WFSSecurityGeocoder.LAYER_POLICIA);
    }
    
    // Guardia Civil
    if (nameLower.includes('guardia civil') || nameLower.includes('cuartel') ||
        nameLower.includes('benemérita')) {
      layerPriority.push(WFSSecurityGeocoder.LAYER_GUARDIA_CIVIL);
    }
    
    // Bomberos
    if (nameLower.includes('bombero') || nameLower.includes('parque') || 
        nameLower.includes('extinción') || nameLower.includes('cpei')) {
      layerPriority.push(WFSSecurityGeocoder.LAYER_BOMBEROS);
    }
    
    // Centro 112 / Emergencias
    if (nameLower.includes('112') || nameLower.includes('emergencia') ||
        nameLower.includes('cecem') || nameLower.includes('cecopal')) {
      layerPriority.push(WFSSecurityGeocoder.LAYER_EMERGENCIAS);
    }

    // Si no hay prioridad específica, orden estándar para PTEL
    if (layerPriority.length === 0) {
      layerPriority.push(
        WFSSecurityGeocoder.LAYER_GUARDIA_CIVIL,  // Más común en municipios pequeños
        WFSSecurityGeocoder.LAYER_BOMBEROS,
        WFSSecurityGeocoder.LAYER_POLICIA,
        WFSSecurityGeocoder.LAYER_EMERGENCIAS
      );
    }

    // Intentar con cada capa (mismo endpoint DERA_g12_servicios)
    const originalLayer = this.config.layerName;

    for (const layer of layerPriority) {
      this.config.layerName = layer;
      
      const result = await this.geocodeLegacy(options);
      
      if (result && result.confidence >= 70) {
        // Restaurar configuración original
        this.config.layerName = originalLayer;
        return result;
      }
    }

    // Restaurar configuración original
    this.config.layerName = originalLayer;
    return null;
  }

  /**
   * Busca instalación de seguridad por código de unidad
   */
  public async geocodeByUnitCode(code: string): Promise<GeocodingResult | null> {
    try {
      const params = this.buildWFSParams({
        name: '',
        unitCode: code,
        maxResults: 10
      } as SecuritySearchOptions);

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      const features = this.parseResponse(response.data);

      if (features.length === 0) {
        return null;
      }

      // Match exacto por código = confianza 100%
      const feature = features[0];
      return this.featureToGeocodingResult(feature, 1.0, feature.name);

    } catch (error) {
      console.error(`Error geocodificando por código ${code}:`, error);
      return null;
    }
  }

  /**
   * Obtiene todas las instalaciones de seguridad de un municipio
   * Incluye: Policía, Guardia Civil, Bomberos, Emergencias
   * 
   * NOTA (F7.T7): Todas las capas están en DERA_g12_servicios
   */
  public async getAllSecurityFacilitiesInMunicipality(
    municipality: string,
    province?: string
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];

    // Todas las capas de seguridad en DERA G12 (F7.T7)
    const securityLayers = [
      WFSSecurityGeocoder.LAYER_GUARDIA_CIVIL,
      WFSSecurityGeocoder.LAYER_BOMBEROS,
      WFSSecurityGeocoder.LAYER_POLICIA,
      WFSSecurityGeocoder.LAYER_EMERGENCIAS
    ];

    const originalLayer = this.config.layerName;

    for (const layer of securityLayers) {
      try {
        this.config.layerName = layer;
        
        const params = this.buildWFSParams({
          name: '',
          municipality,
          province,
          maxResults: 100
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        
        // Añadir metadata de capa a cada feature
        features.forEach(f => {
          f.properties.sourceLayer = layer;
        });
        
        allFeatures.push(...features);

      } catch (error) {
        console.error(`Error obteniendo ${layer} de ${municipality}:`, error);
      }
    }

    // Restaurar configuración original
    this.config.layerName = originalLayer;
    
    return allFeatures;
  }

  /**
   * Obtiene solo parques de bomberos de un municipio/provincia
   * Método específico para emergencias de incendios
   * 
   * NOTA (F7.T7): Usa capa g12_29_ParqueBomberos en DERA_g12_servicios
   */
  public async getFireStationsInArea(
    municipality?: string,
    province?: string,
    bbox?: [number, number, number, number]
  ): Promise<WFSFeature[]> {
    const originalLayer = this.config.layerName;
    
    try {
      this.config.layerName = WFSSecurityGeocoder.LAYER_BOMBEROS;

      const params = this.buildWFSParams({
        name: '',
        municipality,
        province,
        bbox,
        maxResults: 100
      });

      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      return this.parseResponse(response.data);

    } catch (error) {
      console.error('Error obteniendo parques de bomberos:', error);
      return [];
    } finally {
      // Restaurar configuración original
      this.config.layerName = originalLayer;
    }
  }
}
